import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Card, CardBody, CardHeader, DataTable, EmptyState, ErrorState, LoadingState } from "@odingard/design-system";
import type { MeetingCommitment } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, meetingApi } from "../../lib/api";
import { meetingLabel } from "./MeetingShared";

export function CommitmentList() {
  const { activeOrg } = useAuth();
  const request = useAsync(() => meetingApi.commitments(activeOrg?.id ?? ""), [activeOrg?.id]);
  const [members, setMembers] = useState<{ id: string; displayName: string }[]>([]);
  useEffect(() => {
    if (activeOrg?.id) void api.members(activeOrg.id).then((response) => setMembers(response.members)).catch(() => undefined);
  }, [activeOrg?.id]);
  const commitments = request.data?.data.commitments ?? [];
  const personName = (id: string) => members.find((member) => member.id === id)?.displayName ?? (id ? "Former member" : "Unknown member");
  if (request.loading) return <LoadingState label="Loading commitments…" />;
  if (request.error) return <ErrorState description={request.error.message} onRetry={request.reload} />;
  return (
    <>
      <div className="page-head">
        <div><h1>Commitments</h1><p>Confirmed commitments requiring governed follow-through.</p></div>
      </div>
      {commitments.length === 0 ? (
        <EmptyState title="No commitments" description="Confirmed commitments will appear here." />
      ) : (
        <Card>
          <CardHeader title="Commitments" />
          <CardBody>
            <DataTable
              caption="Commitments"
              rows={commitments}
              columns={[
                { key: "statement", header: "Commitment", render: (item: MeetingCommitment) => <Link to={`/studio/meeting-ai/commitments/${item.id}`}>{item.statement}</Link> },
                { key: "ownerUserId", header: "Owner", render: (item: MeetingCommitment) => personName(item.ownerUserId) },
                { key: "dueDate", header: "Due", render: (item: MeetingCommitment) => item.dueDate ?? "No due date" },
                { key: "derivedStatus", header: "Status", render: (item: MeetingCommitment) => <Badge tone={item.derivedStatus === "COMPLETE" ? "success" : "warning"}>{meetingLabel(item.derivedStatus)}</Badge> },
              ]}
            />
          </CardBody>
        </Card>
      )}
    </>
  );
}
